// ---------------------------------------------------------------------------
// CookbookDetail.jsx
// Read-only public view of a single cookbook.
// Authenticated users can follow/unfollow.
// Renders blog-style sections (heading, subtitle, paragraph, recipe blocks).
// Clicking a recipe opens the recipe detail (via onSelectRecipe prop).
// ---------------------------------------------------------------------------

import React from 'react';
import {
  ArrowLeft, BookOpen, Heart, Globe, Lock,
  ChefHat, Clock, Star, Users, LogIn,
} from 'lucide-react';
import { useCookbookDetail, useCookbookFollow } from '../../hooks/useCookbooks';
import { getRecipeImageURL } from '../../hooks/useRecipes';
import { Button } from '../UI';
import { useNavigate } from 'react-router-dom';

// ---------------------------------------------------------------------------
// CookbookRecipeCard — compact card for use inside a cookbook detail
// ---------------------------------------------------------------------------
const CookbookRecipeCard = ({ recipe, onClick }) => {
  const imageURL = getRecipeImageURL(recipe);
  const time     = recipe.totalTimeMinutes
    || ((recipe.prepTimeMinutes || 0) + (recipe.cookTimeMinutes || 0))
    || null;

  return (
    <div
      className="group cursor-pointer rounded-large overflow-hidden bg-surface transition-all hover:shadow-md"
      style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
      onClick={onClick}
    >
      <div className="h-44 relative overflow-hidden bg-gradient-to-br from-primary/8 to-accent/5">
        {imageURL ? (
          <img
            src={imageURL}
            alt={recipe.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ChefHat size={40} className="text-text-tertiary/30" />
          </div>
        )}
        {recipe.dietaryTags?.length > 0 && (
          <div className="absolute bottom-sm left-sm flex gap-xs flex-wrap">
            {recipe.dietaryTags.slice(0, 2).map(tag => (
              <span
                key={tag}
                className="px-xs py-0.5 bg-black/50 backdrop-blur-sm text-white rounded-full text-[10px] font-semibold capitalize"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="p-md">
        <h4 className="text-callout font-semibold text-text-primary line-clamp-2 mb-xs">
          {recipe.title}
        </h4>
        <div className="flex items-center gap-md text-caption text-text-secondary flex-wrap">
          {recipe.rating > 0 && (
            <span className="flex items-center gap-xs">
              <Star size={11} className="text-accent fill-accent" />
              {Number(recipe.rating).toFixed(1)}
            </span>
          )}
          {time > 0 && (
            <span className="flex items-center gap-xs">
              <Clock size={11} />
              {time}m
            </span>
          )}
          {recipe.servings > 0 && (
            <span className="flex items-center gap-xs">
              <Users size={11} />
              {recipe.servings}
            </span>
          )}
          {recipe.difficulty && (
            <span className="capitalize">{recipe.difficulty}</span>
          )}
        </div>
        {recipe.authorName && (
          <p className="text-caption text-text-tertiary mt-xs">by {recipe.authorName}</p>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// SectionBlock — renders a single section in the blog body (read-only)
// Matches iOS CookbookSection.SectionType rendering
// ---------------------------------------------------------------------------
const SectionBlock = ({ section, recipeMap, onSelectRecipe }) => {
  const type = section.type;

  if (type === 'heading') {
    return (
      <h2 className="text-title-2 font-bold text-text-primary leading-snug">
        {section.text}
      </h2>
    );
  }

  if (type === 'subtitle') {
    return (
      <h3 className="text-title-3 font-semibold text-text-secondary leading-snug">
        {section.text}
      </h3>
    );
  }

  if (type === 'paragraph' || type === 'text') {
    return (
      <p className="text-body text-text-secondary leading-relaxed whitespace-pre-wrap">
        {section.text}
      </p>
    );
  }

  if (type === 'recipe' && section.recipeId) {
    const recipe = recipeMap[section.recipeId];
    if (!recipe) return null;
    const imgURL = getRecipeImageURL(recipe);
    const time   = recipe.totalTimeMinutes
      || ((recipe.prepTimeMinutes || 0) + (recipe.cookTimeMinutes || 0))
      || null;

    return (
      <div
        className="group cursor-pointer rounded-large overflow-hidden bg-surface flex hover:shadow-md transition-all"
        style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
        onClick={() => onSelectRecipe && onSelectRecipe(recipe)}
      >
        <div className="w-28 flex-shrink-0 overflow-hidden bg-gradient-to-br from-primary/10 to-accent/5">
          {imgURL
            ? <img src={imgURL} alt={recipe.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
            : <div className="w-full h-full flex items-center justify-center"><ChefHat size={28} className="text-text-tertiary/30" /></div>
          }
        </div>
        <div className="p-md flex-1 min-w-0">
          <p className="text-callout font-semibold text-text-primary line-clamp-2 mb-xs">{recipe.title}</p>
          <div className="flex items-center gap-md text-caption text-text-secondary flex-wrap">
            {recipe.rating > 0 && <span className="flex items-center gap-xs"><Star size={11} className="text-accent fill-accent" />{Number(recipe.rating).toFixed(1)}</span>}
            {time > 0 && <span className="flex items-center gap-xs"><Clock size={11} />{time}m</span>}
            {recipe.servings > 0 && <span className="flex items-center gap-xs"><Users size={11} />{recipe.servings}</span>}
            {recipe.difficulty && <span className="capitalize">{recipe.difficulty}</span>}
          </div>
          {recipe.authorName && <p className="text-caption text-text-tertiary mt-xs">by {recipe.authorName}</p>}
        </div>
      </div>
    );
  }

  return null;
};

// ---------------------------------------------------------------------------
// CookbookDetail
// ---------------------------------------------------------------------------
const CookbookDetail = ({
  cookbookId,
  user,
  onBack,
  onSelectRecipe,
}) => {
  const navigate = useNavigate();

  const { cookbook, recipes, loading, loadingRecipes, error } = useCookbookDetail(cookbookId);
  const { isFollowing, toggle: toggleFollow } = useCookbookFollow(cookbookId, user?.uid || null);

  if (loading) {
    return (
      <div className="text-center py-xxxl">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-md" />
        <p className="text-body text-text-secondary">Loading cookbook...</p>
      </div>
    );
  }

  if (error || !cookbook) {
    return (
      <div className="text-center py-xxxl">
        <BookOpen size={40} className="text-text-tertiary/30 mx-auto mb-md" />
        <p className="text-callout font-semibold text-text-secondary mb-md">
          {error || 'Cookbook not found'}
        </p>
        <Button variant="outline" onClick={onBack}>Go Back</Button>
      </div>
    );
  }

  const count   = cookbook.recipeIds?.length || 0;
  const follows = cookbook.followCount || 0;

  // Build recipe map for section rendering
  const recipeMap = Object.fromEntries(recipes.map(r => [r.firestoreId, r]));

  // Filter sections to only those types we render
  const sections = (cookbook.sections || []).filter(s =>
    ['heading', 'subtitle', 'paragraph', 'text', 'recipe'].includes(s.type)
  );
  const hasSections = sections.length > 0;

  return (
    <div className="space-y-xl">

      {/* Back navigation */}
      <button
        onClick={onBack}
        className="flex items-center gap-sm text-text-secondary hover:text-primary transition-colors"
      >
        <ArrowLeft size={20} />
        <span className="text-callout font-semibold">Cookbooks</span>
      </button>

      {/* Cover image — full-width hero if present */}
      {cookbook.coverImageURL && (
        <div className="w-full h-56 md:h-72 rounded-large overflow-hidden relative">
          <img
            src={cookbook.coverImageURL}
            alt={cookbook.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/15 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-lg">
            <h1 className="text-display font-rounded text-white leading-tight mb-xs">{cookbook.name}</h1>
            <p className="text-caption text-white/75">by {cookbook.ownerName || 'Nutrius User'}</p>
          </div>
        </div>
      )}

      {/* Cookbook header card */}
      <div
        className="p-xl rounded-large bg-surface"
        style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
      >
        <div className="flex items-start justify-between gap-lg flex-wrap">

          <div className="flex-1 min-w-0">
            {/* Visibility badge */}
            <div className="flex items-center gap-sm mb-sm flex-wrap">
              <span className={`inline-flex items-center gap-xs px-sm py-xs rounded-full text-caption font-semibold ${
                cookbook.isPublic
                  ? 'bg-accent/10 text-accent'
                  : 'bg-primary/10 text-primary'
              }`}>
                {cookbook.isPublic ? <Globe size={11} /> : <Lock size={11} />}
                {cookbook.isPublic ? 'Public' : 'Private'}
              </span>
            </div>

            {/* Title — only shown here if no cover image (already shown in hero) */}
            {!cookbook.coverImageURL && (
              <>
                <h1 className="text-display font-rounded text-primary mb-xs">{cookbook.name}</h1>
                <p className="text-body text-text-secondary mb-sm">
                  by {cookbook.ownerName || 'Nutrius User'}
                </p>
              </>
            )}

            {cookbook.description && (
              <p className="text-body text-text-secondary leading-relaxed mb-md max-w-xl">
                {cookbook.description}
              </p>
            )}

            {/* Stats row */}
            <div className="flex items-center gap-lg flex-wrap">
              <span className="flex items-center gap-xs text-caption text-text-secondary">
                <BookOpen size={14} className="text-primary" />
                {count} recipe{count !== 1 ? 's' : ''}
              </span>
              <span className="flex items-center gap-xs text-caption text-text-secondary">
                <Heart size={14} className="text-primary" />
                {follows} follower{follows !== 1 ? 's' : ''}
              </span>
            </div>

            {/* Dietary tags */}
            {cookbook.dietaryTags?.length > 0 && (
              <div className="flex flex-wrap gap-xs mt-md">
                {cookbook.dietaryTags.map(tag => (
                  <span
                    key={tag}
                    className="px-md py-xs bg-primary/8 text-primary rounded-full text-xs font-semibold capitalize"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Follow action */}
          <div className="flex-shrink-0">
            {user ? (
              <button
                onClick={toggleFollow}
                className={`flex items-center gap-sm px-lg py-md rounded-full text-callout font-semibold transition-all ${
                  isFollowing
                    ? 'bg-primary text-white hover:bg-primary/85'
                    : 'bg-primary/8 text-primary hover:bg-primary/15'
                }`}
              >
                <Heart size={16} className={isFollowing ? 'fill-white' : ''} />
                {isFollowing ? 'Following' : 'Follow'}
              </button>
            ) : (
              <div className="flex flex-col items-end gap-sm">
                <p className="text-caption text-text-tertiary">Sign in to follow</p>
                <div className="flex gap-sm">
                  <Button variant="outline" size="small" onClick={() => navigate('/login')}>
                    <LogIn size={14} className="mr-xs" />
                    Sign In
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Blog-style body sections ── */}
      {hasSections && (
        <div className="space-y-lg">
          {sections.map(section => (
            <SectionBlock
              key={section.id}
              section={section}
              recipeMap={recipeMap}
              onSelectRecipe={onSelectRecipe}
            />
          ))}
        </div>
      )}

      {/* ── Recipes grid ── */}
      <div>
        <h2 className="text-title-2 font-bold text-text-primary mb-lg">
          Recipes
          {!loadingRecipes && (
            <span className="text-caption font-normal text-text-tertiary ml-sm">
              ({recipes.length})
            </span>
          )}
        </h2>

        {loadingRecipes ? (
          <div className="text-center py-xl">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
          </div>
        ) : recipes.length === 0 ? (
          <div
            className="rounded-large bg-surface p-xxxl text-center"
            style={{ border: '2px dashed rgba(var(--color-text-tertiary),0.15)' }}
          >
            <BookOpen size={40} className="text-text-tertiary/25 mx-auto mb-md" />
            <p className="text-callout font-semibold text-text-secondary mb-xs">No recipes yet</p>
            <p className="text-caption text-text-tertiary">
              The owner hasn't added any recipes to this cookbook yet.
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-lg">
            {recipes.map(recipe => (
              <CookbookRecipeCard
                key={recipe.firestoreId}
                recipe={recipe}
                onClick={() => onSelectRecipe && onSelectRecipe(recipe)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CookbookDetail;